import React, {useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useDispatch} from 'react-redux'
import {setSelectedCarTypes} from '../../../../features/general/generalSlice'
import Option from '../../../../utils/filters/Option'
import getNewOptionValue from '../../../../utils/filters/getNewOptionValue'
import MultiSelectStatus from '../../ui/MultiSelectStatus'
import {Params} from '../../../../shared/variables'
import {useFeatureServices} from '../../../../use-feature-services'
import {setUrlParams} from '../../../../utils/url/setUrlParams'
import ResponsiveMultiSelect from '../../ui/ResponsiveMultiSelect'

export interface FilterByCarCategoriesProps {
    carTypeOptions: Option
    selectedCarTypes: string[]
    onClick: () => void
}

export default function FilterByCarCategories(
    props: FilterByCarCategoriesProps
): React.JSX.Element {
    const {t} = useTranslation('app')
    const dispatch = useDispatch()
    const {'s2:history': historyService} = useFeatureServices() as any
    const [multiselectStatusMessage, setMultiselectStatusMessage] =
        useState<string>(
            t('filters.noneSelected', {
                title: t('filters.aria-labels.carType'),
            })
        )

    const updateStatusFromValues = (values: string[]) => {
        const selectedLabels = values.map(
            // eslint-disable-next-line security/detect-object-injection
            (key) => props.carTypeOptions[key] || key
        )

        setMultiselectStatusMessage(
            selectedLabels.length > 0
                ? t('filters.statusMessage', {
                      title: t('filters.aria-labels.carType'),
                      values: selectedLabels.join(', '),
                  })
                : t('filters.noneSelected', {
                      title: t('filters.aria-labels.carType'),
                  })
        )
    }

    const appliedFromUrlRef = useRef(false)
    useEffect(() => {
        if (appliedFromUrlRef.current) {
            return
        }
        if (
            !props.carTypeOptions ||
            !Object.keys(props.carTypeOptions).length
        ) {
            return
        }

        const url = new URL(window.location.href)
        const raw =
            url.searchParams.get(Params.CATEGORIES) ??
            url.searchParams.get(`${Params.CATEGORIES}-app`) ??
            ''

        if (!raw) {
            appliedFromUrlRef.current = true

            return
        }

        const urlValues = raw
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)

        const validValues = urlValues.filter((v) =>
            Object.prototype.hasOwnProperty.call(props.carTypeOptions, v)
        )

        if (validValues.length) {
            dispatch(setSelectedCarTypes(validValues))
            updateStatusFromValues(validValues)
        }

        appliedFromUrlRef.current = true
    }, [props.carTypeOptions, dispatch])

    const handleOptionClick = (value: string) => {
        props.onClick()

        const newValue = getNewOptionValue(props.selectedCarTypes, value)
        dispatch(setSelectedCarTypes(newValue))
        updateStatusFromValues(newValue)

        setUrlParams(
            historyService,
            Params.CATEGORIES,
            newValue.length ? newValue.join(',') : ''
        )
    }

    return (
        <>
            <ResponsiveMultiSelect
                name="carType"
                label={t('filters.carType')}
                value={props.selectedCarTypes}
                options={props.carTypeOptions}
                onOptionClick={handleOptionClick}
            />
            {multiselectStatusMessage && (
                <MultiSelectStatus message={multiselectStatusMessage} />
            )}
        </>
    )
}
